import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NoteAlt } from "@mui/icons-material";
import dayjs from "dayjs";
import { saveTrainingDateNotes } from "./api";
import { getTrainingGroupId } from "./types";
import type { TrainingGroup, UpcomingTrainingDate } from "./types";
import DialogHeader from "./DialogHeader";

interface NotesDialogProps {
  target: { group: TrainingGroup; date: UpcomingTrainingDate } | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const NotesDialog: React.FC<NotesDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setNotes(target.date.notes ?? "");
    }
  }, [target]);

  const handleSave = async () => {
    if (!target) return;
    const trainingGroupId = getTrainingGroupId(target.group);
    if (trainingGroupId == null) return;
    try {
      setSaving(true);
      await saveTrainingDateNotes(
        trainingGroupId,
        target.date.training_date,
        notes,
      );
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error saving training date notes:", err);
      onError("Failed to save notes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
    >
      <DialogHeader
        title="Session Notes"
        subtitle={
          target
            ? `${target.group.group_name} · ${dayjs(target.date.training_date).format("ddd DD MMM, HH:mm")}`
            : undefined
        }
        icon={<NoteAlt color="primary" />}
        onClose={onClose}
        closeDisabled={saving}
      />
      <DialogContent dividers>
        <TextField
          label="Notes"
          placeholder="e.g. Bring cones, working on weaves tonight"
          multiline
          rows={4}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={saving}
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <NoteAlt />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Notes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesDialog;
